import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Alert,
  IconButton,
  Stack,
  Divider,
  LinearProgress,
  Autocomplete,
  Grid,
} from "@mui/material";
import {
  Upload,
  Close,
  CheckCircle,
  Description as DescriptionIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { Vocabulary } from "../../Utils/Vocabulary";
import { postData } from "../../Services/postData";
import { getData } from "../../Services/getData";
import { endpoints } from "../../Utils/UrlEnum";
import GenericModal from "../GenericModal";
import { toast } from "react-toastify";
import { AxiosResponse } from "axios";

interface FileWithId {
  id: string;
  file: File;
}

interface CountiesType {
  name: string;
  state_code: string;
}

interface CitiesType {
  name: string;
}

export default function ServiceForm() {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    county: "",
    city: "",
    street: "",
    number: "",
    isLegal: false,
    cui: "",
    defectiveEquipment: "",
    equipmentSeries: "",
    defectDescription: "",
  });

  const [files, setFiles] = useState<FileWithId[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [counties, setCounties] = useState<CountiesType[]>([]);
  const [cities, setCities] = useState<CitiesType[]>([]);

  useEffect(() => {
    getCountiesForRomania();
  }, []);

  const getCountiesForRomania = async () => {
    try {
      const response: AxiosResponse = await getData(
        endpoints.client.addressCheckEndpointState,
      );
      const romaniaData = response?.data?.data?.find(
        (country: any) => country.name === "Romania",
      );
      if (romaniaData && romaniaData.states) {
        const uniqueCounties = romaniaData.states
          .map((state: any) => ({
            name: state.name,
            state_code: state.state_code,
          }))
          .filter(
            (county: CountiesType, index: number, self: CountiesType[]) =>
              index === self.findIndex((c) => c.name === county.name),
          );
        setCounties(uniqueCounties);
      }
    } catch (error) {
      console.error("Error fetching counties:", error);
    }
  };

  const getCitiesForCounty = async (countyName: string) => {
    if (!countyName || countyName.length < 3) return;

    try {
      const response: AxiosResponse = await postData(
        endpoints.client.addressCheckEndpointStateCity,
        {
          country: "Romania",
          state: countyName,
        },
      );
      if (response?.data?.data) {
        const uniqueCities = response.data.data
          .filter(
            (city: string, index: number, self: string[]) =>
              self.indexOf(city) === index,
          )
          .map((city: string) => ({ name: city }));
        setCities(uniqueCities);
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isLegalValue = e.target.value === "true";
    setFormData((prev) => ({
      ...prev,
      isLegal: isLegalValue,
      // Clear CUI if switching to non-legal
      cui: isLegalValue ? prev.cui : "",
    }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = Vocabulary.clientNameIsRequired;
    }

    // Phone validation
    if (!formData.phone.trim()) {
      newErrors.phone = Vocabulary.phoneNumberIsRequired;
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = Vocabulary.emailIsRequired;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = Vocabulary.invalidEmail;
    }

    // Defective equipment validation
    if (!formData.defectiveEquipment.trim()) {
      newErrors.defectiveEquipment = Vocabulary.missingDevice;
    }

    // Equipment series validation
    if (!formData.equipmentSeries.trim()) {
      newErrors.equipmentSeries = Vocabulary.deviceCodeMissing;
    }

    // Defect description validation
    if (!formData.defectDescription.trim()) {
      newErrors.defectDescription = Vocabulary.deviceDescriptionMissing;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const updateMessageBeforeSaving = (data: typeof formData) => {
    // Build message array with proper line breaks
    const messageLines: string[] = [];

    // Build equipment location from parts
    const locationParts = [
      data.county,
      data.city,
      data.street,
      data.number,
    ].filter((part) => part && part.trim());
    const equipmentLocation =
      locationParts.length > 0 ? locationParts.join(", ") : "N/A";

    // Add basic info
    messageLines.push(`${Vocabulary.deviceLocation}: ${equipmentLocation}`);
    messageLines.push(
      `${data.isLegal ? Vocabulary.isLegal : Vocabulary.isNotLegal}`,
    );

    // Add CUI ONLY if isLegal is true
    if (data.isLegal && data.cui.trim()) {
      messageLines.push(`${Vocabulary.CUI}: ${data.cui}`);
    }

    messageLines.push(`${Vocabulary.brokenDevice}: ${data.defectiveEquipment}`);
    messageLines.push(`${Vocabulary.deviceCode}: ${data.equipmentSeries}`);
    messageLines.push(
      `${Vocabulary.deviceBrokenUnit}: ${data.defectDescription}`,
    );

    const newMessage = {
      name: data.name,
      phone: data.phone,
      email: data.email,
      message: [messageLines.join("\n")],
      type: "service",
      status: "necitit",
    };

    return newMessage;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files).map((file) => ({
        id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
        file: file,
      }));
      setFiles((prev) => [...prev, ...newFiles]);
      e.target.value = "";
    }
  };

  const handleRemoveFile = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error(Vocabulary.fillAllFields);
      return;
    }

    setIsSubmitting(true);

    try {
      const messageContent = updateMessageBeforeSaving(formData);

      const data = new FormData();
      data.append("name", messageContent.name);
      data.append("phone", messageContent.phone);
      data.append("email", messageContent.email);
      data.append("message", JSON.stringify(messageContent.message));
      data.append("type", messageContent.type);
      data.append("status", messageContent.status);

      files.forEach((fileWithId) => {
        data.append("files", fileWithId.file);
      });

      const response = await postData(
        `${endpoints.messages.main}/public`,
        data,
      );

      if (response && response.data) {
        setFormData({
          name: "",
          phone: "",
          email: "",
          county: "",
          city: "",
          street: "",
          number: "",
          isLegal: false,
          cui: "",
          defectiveEquipment: "",
          equipmentSeries: "",
          defectDescription: "",
        });
        setFiles([]);
        setErrors({});
        setCities([]);
        setShowSuccessModal(true);
        toast.success(Vocabulary.yourMessageHasBeenSent);
      }
    } catch (error) {
      console.error(Vocabulary.errorSubmittingTheForm, error);
      toast.error(Vocabulary.errorSubmittingTheForm);
    } finally {
      setIsSubmitting(false);
      const messageContent = updateMessageBeforeSaving(formData);
      let customerId;
      try {
        const searchRes = await getData(
          `${endpoints.client.main}/findByEmail/${messageContent.email}`,
        );
        if (searchRes?.data?._id) {
          customerId = searchRes.data._id;
        } else {
          const clientPayload = {
            name: messageContent.name,
            email: messageContent.email,
            phone: messageContent.phone,
            isLegalPerson: formData.isLegal,
            cui: formData.cui,
          };
          if (formData.cui === "") {
            delete clientPayload.cui;
          }
          const createRes = await postData(
            `${endpoints.client.main}`,
            clientPayload,
          );
          customerId = createRes?.data?._id;
        }
      } catch (err) {
        toast.error("Error finding or creating customer");
      }

      const formDataToSend = new FormData();
      formDataToSend.append("customerId", customerId || "");
      formDataToSend.append("message", JSON.stringify(messageContent.message));
      files.forEach((fileWithId) => {
        formDataToSend.append("files", fileWithId.file);
      });

      await postData(
        `https://erp.cafe-mag.ro/api/external-order`,
        formDataToSend,
      )
        .then(() => {
          window.location.replace("https://cafe-mag.ro/");
        })
        .catch((err) => {
          toast.error(`${err}`);
        });
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.primary.main}25 100%)`,
        py: 4,
        px: 2,
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={6}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            backgroundColor: theme.palette.background.paper,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
              color: "#fff",
              p: 4,
              textAlign: "center",
            }}
          >
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="center"
              sx={{ mb: 2 }}
            >
              <Box
                sx={{
                  backgroundColor: "rgba(255,255,255,0.2)",
                  borderRadius: "50%",
                  p: 1.5,
                  display: "flex",
                }}
              >
                <DescriptionIcon sx={{ fontSize: 32 }} />
              </Box>
              <Typography variant="h4" fontWeight={700}>
                {Vocabulary.serviceFormRequest}
              </Typography>
            </Stack>
          </Box>

          {/* Form Content */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ p: { xs: 3, md: 4 } }}
          >
            {/* Info Box */}
            <Alert
              severity="info"
              sx={{
                mb: 4,
                borderRadius: 2,
                "& .MuiAlert-message": { width: "100%" },
              }}
            >
              <Typography variant="body2" sx={{ textAlign: "justify" }}>
                {Vocabulary.formDescription}
              </Typography>
            </Alert>

            <Stack spacing={3}>
              {/* Name */}
              <TextField
                fullWidth
                required
                label={Vocabulary.ownerName}
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                error={Boolean(errors.name)}
                helperText={errors.name || Vocabulary.descriptionForOwner}
                inputProps={{ maxLength: 255 }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                align="right"
              >
                {formData.name.length}/255
              </Typography>

              {/* Phone */}
              <TextField
                fullWidth
                required
                label={Vocabulary.phoneNumber}
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleInputChange}
                error={Boolean(errors.phone)}
                helperText={errors.phone || Vocabulary.phoneNumberDescription}
                placeholder="+40 123 123 123"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />

              {/* Email */}
              <TextField
                fullWidth
                required
                label={Vocabulary.emailAddress}
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                error={Boolean(errors.email)}
                helperText={errors.email || Vocabulary.emailAddressDescription}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />

              <Divider sx={{ my: 2 }} />

              {/* Entity Type */}
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
                  {Vocabulary.companyOrPerson}
                </FormLabel>
                <RadioGroup
                  row
                  name="isLegal"
                  value={formData.isLegal.toString()}
                  onChange={handleRadioChange}
                >
                  <FormControlLabel
                    value="false"
                    control={<Radio color="primary" />}
                    label={Vocabulary.isNotLegal}
                  />
                  <FormControlLabel
                    value="true"
                    control={<Radio color="primary" />}
                    label={Vocabulary.isLegal}
                  />
                </RadioGroup>
              </FormControl>

              {/* CUI - Only show if isLegal is true */}
              {formData.isLegal && (
                <TextField
                  fullWidth
                  label={Vocabulary.CUI}
                  name="cui"
                  value={formData.cui}
                  onChange={handleInputChange}
                  helperText={Vocabulary.CUIPlaceholder}
                  placeholder="RO12345678"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              )}

              <Divider sx={{ my: 2 }} />

              {/* Equipment Location Section */}
              <Box sx={{ width: "100%" }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {Vocabulary.deviceLocation}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 2 }}
                >
                  {Vocabulary.deviceLocationDescription}
                </Typography>

                <Grid container spacing={2}>
                  {/* County Autocomplete */}
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      id="county"
                      freeSolo
                      fullWidth
                      noOptionsText={Vocabulary.noResultsFound}
                      options={counties || []}
                      value={
                        counties.find((c) => c.name === formData.county) || null
                      }
                      onChange={(_, newValue) => {
                        const countyName =
                          typeof newValue === "string"
                            ? newValue
                            : newValue?.name || "";
                        setFormData((prev) => ({
                          ...prev,
                          county: countyName,
                          city: "", // Reset city when county changes
                        }));
                        setCities([]); // Clear cities
                        // Only fetch cities if a county was selected from the dropdown (not typed)
                        if (countyName && typeof newValue === "object") {
                          getCitiesForCounty(countyName);
                        }
                      }}
                      onInputChange={(_, newValue) => {
                        setFormData((prev) => ({
                          ...prev,
                          county: newValue,
                          city: "", // Reset city when county changes
                        }));
                        setCities([]); // Clear cities
                      }}
                      getOptionLabel={(option: any) => {
                        if (typeof option === "string") return option;
                        if (option?.name?.includes("County")) {
                          return option.name.split(" ")[0];
                        }
                        return option?.name || "";
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={Vocabulary.county}
                          fullWidth
                          variant="outlined"
                          InputLabelProps={{
                            shrink: true,
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": { borderRadius: 2 },
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* City Autocomplete */}
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      id="city"
                      freeSolo
                      fullWidth
                      noOptionsText={Vocabulary.noResultsFound}
                      options={cities || []}
                      value={
                        formData.city
                          ? cities.find((c) => c.name === formData.city) ||
                            formData.city
                          : null
                      }
                      inputValue={formData.city}
                      onChange={(_, newValue) => {
                        const cityName =
                          typeof newValue === "string"
                            ? newValue
                            : newValue?.name || "";
                        setFormData((prev) => ({
                          ...prev,
                          city: cityName,
                        }));
                      }}
                      onInputChange={(_, newValue) => {
                        setFormData((prev) => ({
                          ...prev,
                          city: newValue,
                        }));
                      }}
                      getOptionLabel={(option: any) => {
                        if (typeof option === "string") return option;
                        return option?.name || "";
                      }}
                      disabled={!formData.county}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={Vocabulary.city}
                          fullWidth
                          variant="outlined"
                          InputLabelProps={{
                            shrink: true,
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": { borderRadius: 2 },
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Street TextField */}
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      label={Vocabulary.street}
                      name="street"
                      value={formData.street}
                      onChange={handleInputChange}
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>

                  {/* Number TextField */}
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label={Vocabulary.streetNumber}
                      name="number"
                      value={formData.number}
                      onChange={handleInputChange}
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Defective Equipment */}
              <TextField
                fullWidth
                required
                label={Vocabulary.brokenDevice}
                name="defectiveEquipment"
                value={formData.defectiveEquipment}
                onChange={handleInputChange}
                error={Boolean(errors.defectiveEquipment)}
                helperText={
                  errors.defectiveEquipment || Vocabulary.deviceDescription
                }
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />

              {/* Equipment Series */}
              <TextField
                fullWidth
                required
                label={Vocabulary.deviceCode}
                name="equipmentSeries"
                value={formData.equipmentSeries}
                onChange={handleInputChange}
                error={Boolean(errors.equipmentSeries)}
                helperText={
                  errors.equipmentSeries || Vocabulary.deviceCodeDescription
                }
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />

              {/* Defect Description */}
              <TextField
                fullWidth
                required
                multiline
                rows={5}
                label={Vocabulary.deviceBrokenUnit}
                name="defectDescription"
                value={formData.defectDescription}
                onChange={handleInputChange}
                error={Boolean(errors.defectDescription)}
                helperText={
                  errors.defectDescription ||
                  Vocabulary.deviceBrokenUnitDescription
                }
                inputProps={{ maxLength: 2000 }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                align="right"
              >
                {formData.defectDescription.length}/2000
              </Typography>

              <Divider sx={{ my: 2 }} />

              {/* File Upload */}
              <Box sx={{ width: "100%" }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {Vocabulary.formAttachments}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 2 }}
                >
                  {Vocabulary.formAttachmentsDescription}
                </Typography>

                <Box
                  component="label"
                  sx={{
                    display: "block",
                    width: "100%",
                    border: `2px dashed ${theme.palette.divider}`,
                    borderRadius: 2,
                    p: 4,
                    textAlign: "center",
                    backgroundColor:
                      theme.palette.mode === "light"
                        ? "#fafafa"
                        : "rgba(255,255,255,0.05)",
                    cursor: "pointer",
                    transition: "all 0.3s",
                    padding: 0,
                    "&:hover": {
                      borderColor: theme.palette.primary.main,
                      backgroundColor:
                        theme.palette.mode === "light"
                          ? `${theme.palette.primary.light}15`
                          : "rgba(255,255,255,0.08)",
                    },
                  }}
                >
                  <input
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    accept="image/*,.pdf,.doc,.docx,.txt"
                    hidden
                  />
                  <Upload
                    sx={{
                      fontSize: 48,
                      color: theme.palette.primary.light,
                      mb: 1,
                    }}
                  />
                  <Typography variant="body2" color="primary" fontWeight={600}>
                    {Vocabulary.chooseFileToUpload}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {Vocabulary.dragAndDropAlternative}
                  </Typography>
                </Box>

                {files.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ mb: 1 }}
                    >
                      <CheckCircle color="success" fontSize="small" />
                      <Typography
                        variant="body2"
                        color="success.main"
                        fontWeight={600}
                      >
                        {files.length} {Vocabulary.filesSelected}
                      </Typography>
                    </Stack>
                    <Stack spacing={1}>
                      {files.map((fileWithId) => (
                        <Paper
                          key={fileWithId.id}
                          elevation={0}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 1.5,
                            backgroundColor:
                              theme.palette.mode === "light"
                                ? "#f5f5f5"
                                : "rgba(255,255,255,0.05)",
                            borderRadius: 1.5,
                            border: `1px solid ${theme.palette.divider}`,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flex: 1,
                              overflow: "hidden",
                            }}
                          >
                            <DescriptionIcon fontSize="small" color="action" />
                            <Box sx={{ overflow: "hidden" }}>
                              <Typography variant="body2" noWrap>
                                {fileWithId.file.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {(fileWithId.file.size / 1024).toFixed(2)} KB
                              </Typography>
                            </Box>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveFile(fileWithId.id)}
                            sx={{ color: "error.main" }}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Delivery Info */}
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                <Stack spacing={0.5}>
                  <Typography variant="body2">
                    {Vocabulary.deliveryMessagePart1}
                  </Typography>
                  <Typography variant="body2">
                    {Vocabulary.deliveryMessagePart2}
                  </Typography>
                  <Typography variant="body2">
                    {Vocabulary.deliveryMessagePart3}
                  </Typography>
                </Stack>
              </Alert>

              {/* Submit Button */}
              <Box sx={{ pt: 2 }}>
                {isSubmitting && (
                  <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />
                )}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isSubmitting}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 700,
                    fontSize: "1.1rem",
                    background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                    boxShadow: 4,
                    color: "#fff",
                    "&:hover": {
                      boxShadow: 8,
                      transform: "translateY(-2px)",
                      transition: "all 0.3s",
                    },
                  }}
                >
                  {isSubmitting ? Vocabulary.sending : Vocabulary.sendForm}
                </Button>
              </Box>
            </Stack>
          </Box>
        </Paper>
      </Container>

      {/* Success Modal */}
      <GenericModal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title={Vocabulary.successServiceModal}
      >
        <Box sx={{ p: 3, textAlign: "center" }}>
          <CheckCircle sx={{ fontSize: 64, color: "success.main", mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {Vocabulary.yourMessageHasBeenSent}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {Vocabulary.weWillContactYouSoon}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setShowSuccessModal(false)}
            sx={{ borderRadius: 2, px: 4 }}
          >
            {Vocabulary.close}
          </Button>
        </Box>
      </GenericModal>
    </Box>
  );
}
